import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { CartService } from '../../../../shared/services/cart.service';
import { CheckoutStateService } from '../../../../shared/services/checkout-state.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../shared/services/auth.service';
import { PaynowService } from '../../../../shared/services/paynow.service';
import { NewsletterService } from '../../../../shared/services/newsletter.service';
import { ShippingStateService } from '../../../../shared/services/shipping-state.service';
import { CurrencyService } from '../../../../core/services/currency.service';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  getCartItemMaximumQuantity,
  getCartItemMinimumQuantity,
  getCartItemQuantityError,
  getCartItemUnitPriceHT,
  getCartTaxEur,
  MAX_CART_LINE_ITEMS,
} from '../../../../shared/cart-pricing.util';
import { getCheckoutDraftStorageKey } from '../../../../shared/checkout-draft.util';
import {
  getUserCheckoutAddresses,
  getUserCheckoutBuyerType,
} from '../../../../shared/checkout-address.util';

/**
 * Validates international telephone numbers dynamically using libphonenumber-js.
 */
export function internationalPhoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = (control.value || '').trim();
    if (!val) return null;
    try {
      return isValidPhoneNumber(val) ? null : { invalidInternationalPhone: true };
    } catch (_) {
      return { invalidInternationalPhone: true };
    }
  };
}

@Component({
  selector: 'app-stepper-container',
  standalone: false,
  templateUrl: './stepper-container.component.html',
  styleUrl: './stepper-container.component.scss',
})
export class StepperContainerComponent implements OnInit, OnDestroy {
  private readonly voucherAppliedListener = (event: Event): void => {
    const detail = (event as CustomEvent<{
      discountAmount: number;
      voucher: any;
    }>).detail;
    if (!detail) return;
    this.voucherDiscountAmount = detail.discountAmount;
    this.appliedVoucher = detail.voucher;
  };
  private checkoutDraftStorageKey = '';
  private cartFingerprint: string | null = null;
  cart: any = [];
  cartNotice: string | null = null;
  voucherDiscountAmount: number = 0;
  appliedVoucher: any = null;
  isProcessingOrder = false; // Add loading state for order processing
  paymentError: string | null = null;
  steps = [
    { id: 1, name: 'Koszyk' },
    { id: 2, name: 'Logowanie / zakup bez konta' },
    { id: 3, name: 'Dane klienta' },
    { id: 4, name: 'Podsumowanie zamówienia' },
  ];

  currentStep = 1; // Start with Shopping Basket
  private stepSubscription: Subscription;
  private authSubscription?: Subscription;
  private cartSubscription?: Subscription;
  private checkoutDraftSubscription?: Subscription;
  private cartNoticeSubscription?: Subscription;

  constructor(
    private readonly cartService: CartService,
    private readonly fb: FormBuilder,
    private readonly checkoutStateService: CheckoutStateService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly paynowService: PaynowService,
    private readonly newsletterService: NewsletterService,
    public readonly shippingStateService: ShippingStateService,
    public readonly currencyService: CurrencyService
  ) {
    this.stepSubscription = this.checkoutStateService.currentStep$.subscribe(step => {
      this.currentStep = step;
      this.cdr.markForCheck(); // Safe for both creation and update mode
    });
  }
  checkoutForm!: FormGroup;

  ngOnInit(): void {
    this.getCartContent();
    this.checkoutForm = this.checkoutFormBuilder();
    try {
      sessionStorage.removeItem('brocom_checkout_draft');
    } catch (error) {
      console.warn('Could not clear the legacy checkout draft:', error);
    }
    this.checkoutDraftStorageKey = getCheckoutDraftStorageKey(
      this.getStoredUser(),
    );
    this.restoreCheckoutDraft();
    this.checkoutDraftSubscription = this.step2FormGroup.valueChanges.subscribe(() => {
      this.saveCheckoutDraft();
    });
    this.populateCartFormArray();
    this.cartSubscription = this.cartService.cart$.subscribe((cart) => {
      const fingerprint = this.getCartFingerprint(cart);
      if (this.cartFingerprint !== null && fingerprint !== this.cartFingerprint) {
        this.appliedVoucher = null;
        this.voucherDiscountAmount = 0;
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('voucherApplied', {
            detail: { discountAmount: 0, voucher: null },
          }));
        }
      }
      this.cartFingerprint = fingerprint;
      this.cart = cart;
      this.populateCartFormArray();
      this.cdr.markForCheck();
    });
    this.cartNoticeSubscription = this.cartService.cartNotice$.subscribe((notice) => {
      this.cartNotice = notice;
      this.cdr.markForCheck();
    });
    // Listen for voucher changes from shopping cart step (example event wiring)
    window.addEventListener('voucherApplied', this.voucherAppliedListener);

    // Keep an intentional login/details step, but don't carry a stale summary
    // step into a fresh checkout route.
    if (this.currentStep < 1 || this.currentStep > 3) {
      this.checkoutStateService.setCurrentStep(1);
    }

    // Check if user is already authenticated when initializing checkout
    this.handleAuthenticationState();

    // Also check auth state after a short delay to handle page refresh scenarios
    setTimeout(() => {
      this.handleAuthenticationState();
    }, 200);
  }

  private getStoredUser(): any {
    if (!this.authService.isLoggedIn()) return null;

    try {
      const u = this.authService.getCurrentUser();
      if (u) return u;
      const str = localStorage.getItem('clientUser') || sessionStorage.getItem('clientUser');
      if (str) return JSON.parse(str);
    } catch (e) { }
    return null;
  }

  private switchCheckoutDraft(user: any): void {
    const nextStorageKey = getCheckoutDraftStorageKey(user);
    if (nextStorageKey === this.checkoutDraftStorageKey) return;

    if (this.checkoutDraftStorageKey) {
      try {
        sessionStorage.removeItem(this.checkoutDraftStorageKey);
      } catch (error) {
        console.warn('Could not clear the previous checkout draft:', error);
      }
    }
    this.checkoutDraftStorageKey = nextStorageKey;
    this.step2FormGroup.reset(undefined, { emitEvent: false });
    this.restoreCheckoutDraft();
  }

  private populateStep2FromUser(user: any): void {
    if (!user) return;
    const step2 = this.checkoutForm.get('step2') as FormGroup;
    if (!step2) return;

    const addresses = getUserCheckoutAddresses(user);
    const companyName = user.company || user.organizationName || '';
    const state = user.stateProvince || user.state || '';
    const { street, postalCode, city, country } = addresses.billing;
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const phone = user.phone || user.mobileNumber || '';
    const email = user.email || '';
    const vatNumber = user.vatNumber || user.vat_number || '';
    const department = user.department || user.industrysector || '';

    const buyerType = getUserCheckoutBuyerType(user);
    const isCompany = buyerType === 'company';

    const patch: any = {};
    patch.accountType = buyerType;
    if (isCompany) {
      if (companyName) patch.companyName = companyName;
      if (department) patch.industrysector = department;
      if (vatNumber) patch.vatNumber = vatNumber;
    } else {
      patch.companyName = '';
      patch.industrysector = '';
      patch.vatNumber = '';
      patch.orderingParty = '';
      patch.departmentOrder = '';
    }
    if (street) patch.postcodeTown = street;
    if (state) patch.stateProvince = state;
    if (postalCode) patch.postalCode = postalCode;
    if (city) patch.city = city;
    // Account country ALWAYS takes priority over sidebar selection
    if (country) patch.otherCountry = country;
    if (firstName) patch.firstName = firstName;
    if (lastName) patch.lastName = lastName;
    if (phone) patch.phoneNumber = phone;
    if (email) patch.email = email;
    patch.deliverySameAsBilling = addresses.deliverySameAsBilling;
    if (!addresses.deliverySameAsBilling) {
      patch.deliveryRecipientName = addresses.deliveryRecipient;
      patch.deliveryCompanyName = addresses.deliveryCompany;
      patch.deliveryAddress = addresses.delivery.street;
      patch.deliveryPostalCode = addresses.delivery.postalCode;
      patch.deliveryCity = addresses.delivery.city;
      patch.deliveryCountry = addresses.delivery.country;
      patch.deliveryPhone = addresses.deliveryPhone;
    }

    const safePatch = Object.fromEntries(
      Object.entries(patch).filter(
        ([key]) => key === 'accountType' || !step2.get(key)?.dirty,
      ),
    );
    if (Object.keys(safePatch).length > 0) {
      step2.patchValue(safePatch);
    }
  }

  private handleAuthenticationState(): void {
    const user = this.getStoredUser();
    if (user) {
      this.populateStep2FromUser(user);
    }

    const isAuthenticated = this.authService.isLoggedIn();

    // Fetch full fresh profile from backend to ensure all account fields (company, address, country) are up to date
    if (isAuthenticated) {
      this.authService.getProfile().subscribe({
        next: (response: any) => {
          const profileUser = response?.data?.[0]?.user || response?.user;
          if (profileUser) {
            const current = this.getStoredUser() || {};
            const merged = { ...current, ...profileUser };
            try {
              localStorage.setItem('clientUser', JSON.stringify(merged));
            } catch (e) { }
            this.populateStep2FromUser(merged);
          }
        },
        error: (err) => console.warn('Could not fetch user profile in stepper:', err)
      });
    }

    // If user is authenticated and we're on step 2 (Login/Guest), skip to Customer Details (step 3)
    if (isAuthenticated && this.currentStep === 2) {
      setTimeout(() => {
        this.checkoutStateService.setCurrentStep(3);
        this.cdr.detectChanges();
      }, 50);
    }

    // Also handle the case where user just returned from registration/login
    // by subscribing to auth state changes
    if (!this.authSubscription) {
      this.authSubscription = this.authService.currentUser$.subscribe(currentUser => {
        const effectiveUser = currentUser || this.getStoredUser();
        this.switchCheckoutDraft(effectiveUser);
        if (effectiveUser) {
          this.populateStep2FromUser(effectiveUser);
          if (this.currentStep === 2) {
            setTimeout(() => {
              this.checkoutStateService.setCurrentStep(3);
              this.cdr.detectChanges();
            }, 50);
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.stepSubscription.unsubscribe();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    this.cartSubscription?.unsubscribe();
    this.checkoutDraftSubscription?.unsubscribe();
    this.cartNoticeSubscription?.unsubscribe();
    window.removeEventListener('voucherApplied', this.voucherAppliedListener);
  }

  @HostListener('window:pageshow', ['$event'])
  handlePageShow(event: PageTransitionEvent): void {
    if (!event.persisted) return;

    this.isProcessingOrder = false;
    this.cdr.detectChanges();
  }

  private restoreCheckoutDraft(): void {
    try {
      const storedDraft = sessionStorage.getItem(this.checkoutDraftStorageKey);
      if (!storedDraft) return;
      const draft = JSON.parse(storedDraft);
      if (!draft || typeof draft !== 'object' || Array.isArray(draft)) {
        throw new Error('Saved checkout details have an invalid format.');
      }
      const safeDraft: Record<string, any> = Object.create(null);
      const controls = this.step2FormGroup.controls;
      Object.keys(draft).forEach(key => {
        if (Object.hasOwn(draft, key) && Object.hasOwn(controls, key)) {
          safeDraft[key] = draft[key];
        }
      });
      this.step2FormGroup.patchValue(safeDraft, { emitEvent: false });
      Object.keys(safeDraft).forEach((key) => {
        this.step2FormGroup.get(key)?.markAsDirty({ onlySelf: true });
      });
    } catch (error) {
      console.warn('Could not restore saved checkout details:', error);
      sessionStorage.removeItem(this.checkoutDraftStorageKey);
    }
  }

  private saveCheckoutDraft(): void {
    try {
      sessionStorage.setItem(
        this.checkoutDraftStorageKey,
        JSON.stringify(this.step2FormGroup.getRawValue()),
      );
    } catch (error) {
      console.warn('Could not save checkout details for payment retry:', error);
    }
  }

  populateCartFormArray(): void {
    const step1Group = this.checkoutForm.get('step1') as FormGroup;
    const itemsFormArray = step1Group.get('items') as FormArray;

    while (itemsFormArray.length > 0) {
      itemsFormArray.removeAt(0);
    }

    this.cart.forEach((cartItem: any) => {
      // Try to find the correct price field
      const price = this.getItemPrice(cartItem);

      // Debug logs removed

      const itemGroup = this.fb.group({
        productId: [cartItem.product?.id],
        productLabel: [cartItem.product?.label],
        quantity: [cartItem.quantity, [Validators.required, Validators.min(1)]],
        unitPrice: [price],
        totalPrice: [price * cartItem.quantity],
        combination: [cartItem?.combination],
      });

      itemsFormArray.push(itemGroup);
    });

    this.updateStep1TotalPrice();
  }

  private getItemPrice(item: any): number {
    return getCartItemUnitPriceHT(item);
  }

  updateStep1TotalPrice(): void {
    const itemsFormArray = this.checkoutForm.get('step1.items') as FormArray;
    const total = itemsFormArray.controls.reduce((sum, item) => {
      return sum + (item.get('totalPrice')?.value || 0);
    }, 0);

    this.checkoutForm.get('step1.totalPrice')?.setValue(total);
  }

  private getInitialCountry(): string {
    const saved = localStorage.getItem('brocom_selected_country');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed?.name || '';
      } catch (e) { }
    }
    return '';
  }

  checkoutFormBuilder(): FormGroup {
    return this.fb.group({
      step1: this.fb.group({
        totalPrice: [0, Validators.required],
        items: this.fb.array([]),
      }),
      step2: this.fb.group({
        // Company Information
        accountType: ['individual'],
        companyName: [''], // Required only for business accounts (set dynamically in billing-step)
        industrysector: [''], // Now optional
        vatNumber: [''],

        // Address Information
        postcodeTown: ['', Validators.required],

        // Other Address Information
        stateProvince: [''], // Optional
        postalCode: ['', Validators.required],
        city: ['', [Validators.required, Validators.minLength(2)]],
        otherCountry: [this.getInitialCountry(), Validators.required], // Auto-detected / selected country
        otherAddressInfo: [''], // Optional

        // Contact Information
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        phoneNumber: ['', [Validators.required, internationalPhoneValidator()]],
        faxNumber: [''],
        email: ['', [Validators.required, Validators.email]],

        // Other Details
        orderingParty: [''],
        noteBrocom: [''],
        departmentOrder: [''],
        deliverySameAsBilling: [true],
        deliveryRecipientName: [''],
        deliveryCompanyName: [''],
        deliveryAddress: [''],
        deliveryPostalCode: [''],
        deliveryCity: [''],
        deliveryStateProvince: [''],
        deliveryCountry: [''],
        deliveryPhone: [''],
      }),
      step4: this.fb.group({
        // Legal acceptance before placing an order
        acceptTerms: [false, Validators.requiredTrue],
        subscribeNewsletter: [false], // Optional
      }),
    });
  }
  get step2FormGroup(): FormGroup {
    return this.checkoutForm.get('step2') as FormGroup;
  }

  get step4FormGroup(): FormGroup {
    return this.checkoutForm.get('step4') as FormGroup;
  }

  get isNextDisabled(): boolean {
    if (this.currentStep === 1) {
      return !this.cart || this.cart.length === 0;
    }

    if (this.currentStep === 3) {
      const step2 = this.checkoutForm?.get('step2') as FormGroup;
      if (!step2) return true;

      // Keep Next actionable so it can reveal field errors on an invalid form.
      if (step2.invalid) return false;

      const deliverySameAsBilling =
        step2.get('deliverySameAsBilling')?.value !== false;
      const addressControls = deliverySameAsBilling
        ? ['otherCountry', 'postcodeTown', 'postalCode', 'city']
        : ['deliveryCountry', 'deliveryAddress', 'deliveryPostalCode', 'deliveryCity'];
      const [countryControl, streetControl, postalCodeControl, cityControl] =
        addressControls.map((name) => step2.get(name));
      const country = String(countryControl?.value || '').trim();
      const street = String(streetControl?.value || '').trim();
      const postalCode = String(postalCodeControl?.value || '').trim();
      const city = String(cityControl?.value || '').trim();

      // Required address fields
      if (!country || !street || !postalCode || !city || city.length < 2) {
        return false;
      }

      // Postal code validity check (Zippopotam)
      if (postalCodeControl?.hasError('invalidPostalCode')) {
        return true;
      }

      // Shipping calculation check (FedEx rates)
      const shipping = this.shippingStateService.currentState;
      if (shipping.loading) return true;
      if (shipping.error) return true;
      if (!shipping.options || shipping.options.length === 0) return true;

      return false;
    }

    if (this.currentStep === 4) {
      const step4 = this.checkoutForm?.get('step4') as FormGroup;
      if (!step4 || step4.invalid) return true;
      const shipping = this.shippingStateService.currentState;
      if (shipping.loading || shipping.error || !shipping.selectedOption) return true;
      return false;
    }

    return false;
  }

  nextStep(): void {
    if (this.isNextDisabled) {
      if (this.currentStep === 3) {
        const step2Form = this.checkoutForm.get('step2') as FormGroup;
        if (step2Form) {
          this.markFormGroupTouched(step2Form);
        }
      }
      return;
    }

    if (this.currentStep === 1) {
      const cartValidationError = this.getCartValidationError();
      if (cartValidationError) {
        this.paymentError = cartValidationError;
        this.cdr.markForCheck();
        return;
      }
    }

    // Validate current step before proceeding
    if (this.currentStep === 1) {
      // Validate shopping basket - ensure cart has items
      if (!this.cart || this.cart.length === 0) {
        return; // Don't proceed if cart is empty
      }
    }

    if (this.currentStep === 3) {
      // Validate billing details form
      const step2Form = this.checkoutForm.get('step2') as FormGroup;
      if (step2Form && step2Form.invalid) {
        // Mark all fields as touched to show validation errors
        this.markFormGroupTouched(step2Form);
        return; // Don't proceed if form is invalid
      }

      const deliverySameAsBilling =
        step2Form.get('deliverySameAsBilling')?.value !== false;
      const deliveryControls = deliverySameAsBilling
        ? []
        : ['deliveryCountry', 'deliveryAddress', 'deliveryPostalCode', 'deliveryCity'];
      const hasIncompleteDeliveryAddress = deliveryControls.some((name) =>
        !String(step2Form.get(name)?.value || '').trim(),
      );
      if (hasIncompleteDeliveryAddress) {
        deliveryControls.forEach((name) => step2Form.get(name)?.markAsTouched());
        return;
      }
    }

    // Handle order completion when on the last step
    if (this.currentStep === this.steps.length) {
      this.completeOrder();
      return;
    }

    if (this.currentStep < this.steps.length) {
      // If user is already authenticated and on step 1 (Shopping Basket), skip step 2 (Login/Guest) directly to step 3 (Customer Details)
      if (this.currentStep === 1 && this.authService.isLoggedIn()) {
        this.checkoutStateService.setCurrentStep(3);
      } else {
        const newStep = this.currentStep + 1;
        this.checkoutStateService.setCurrentStep(newStep);
      }
      this.scrollToTop();
    }
  }

  private getCartDiscountedPriceHT(item: any): number {
    return getCartItemUnitPriceHT(item);
  }

  getOrderSubtotalEur(): number {
    if (!this.cart || this.cart.length === 0) return 0;
    return this.cart.reduce((total: number, item: any) => {
      const discountedPriceHT = this.getCartDiscountedPriceHT(item);
      const quantity = Number(item?.quantity) || 0;
      return total + discountedPriceHT * quantity;
    }, 0);
  }

  private getCartFingerprint(cart: any[]): string {
    return JSON.stringify(
      cart.map((item) => ({
        id: item?.id,
        productId: item?.product?.id,
        quantity: item?.quantity,
        priceHT: item?.product?.priceHT,
        discountRate: item?.product?.discountRate,
        tvaRate: item?.product?.tvaRate,
        volumeDiscounts: item?.product?.volumeDiscounts,
        selectedDownloads: (item?.selectedDownloads || [])
          .map((download: any) => String(download?.id))
          .sort(),
        selectedColorId: item?.selectedColor?.id,
        variants: Object.keys(item?.variants || {})
          .sort()
          .map((key) => [key, item.variants[key]?.id, item.variants[key]?.value]),
      })),
    );
  }

  private getCartValidationError(): string | null {
    if (!Array.isArray(this.cart) || this.cart.length === 0) {
      return 'Koszyk jest pusty.';
    }
    if (this.cart.length > MAX_CART_LINE_ITEMS) {
      return `Koszyk może zawierać maksymalnie ${MAX_CART_LINE_ITEMS} pozycji.`;
    }

    for (const item of this.cart) {
      const quantityError = getCartItemQuantityError(item);
      if (quantityError) {
        const productName = item?.product?.label || item?.product?.name || 'Produkt';
        return `${productName}: ${quantityError}`;
      }
    }

    return null;
  }

  getOrderSubtotal(): number {
    return this.currencyService.convertFromEur(this.getOrderSubtotalEur());
  }

  getOrderTaxEur(): number {
    return getCartTaxEur(this.cart || []);
  }

  getOrderTax(): number {
    return this.currencyService.convertFromEur(this.getOrderTaxEur());
  }

  getOrderDownloadsEur(): number {
    if (!this.cart || this.cart.length === 0) return 0;
    return this.cart.reduce((total: number, item: any) => {
      if (!item?.selectedDownloads || item.selectedDownloads.length === 0) return total;
      const downloadsTotal = item.selectedDownloads.reduce((sum: number, d: any) => sum + (parseFloat(d.price) || 0), 0);
      return total + downloadsTotal;
    }, 0);
  }

  getOrderDownloads(): number {
    return this.currencyService.convertFromEur(this.getOrderDownloadsEur());
  }

  getOrderShipping(): number {
    return this.currencyService.convertShipping(
      this.shippingStateService.currentPrice || 0,
      this.shippingStateService.currentState.currency
    );
  }

  getOrderDiscount(): number {
    return this.currencyService.convertFromEur(this.voucherDiscountAmount || 0);
  }

  /**
   * Calculate total order amount in active currency: Subtotal + Tax + Downloads + FedEx Shipping - Voucher Discount
   */
  getOrderTotal(): number {
    const subtotal = this.getOrderSubtotal();
    const tax = this.getOrderTax();
    const downloads = this.getOrderDownloads();
    const shipping = this.getOrderShipping();
    const discount = this.getOrderDiscount();
    let total = subtotal + tax + downloads + shipping;
    if (discount > 0) {
      total = Math.max(0, total - discount);
    }
    return Number(total.toFixed(2));
  }

  completeOrder(): void {
    // Prevent multiple submissions
    if (this.isProcessingOrder) {
      return;
    }

    const cartValidationError = this.getCartValidationError();
    if (cartValidationError) {
      this.paymentError = cartValidationError;
      this.cdr.markForCheck();
      return;
    }

    // Validate the final order summary form if needed
    const orderSummaryForm = this.checkoutForm.get('step4') as FormGroup;
    if (orderSummaryForm && orderSummaryForm.invalid) {
      this.markFormGroupTouched(orderSummaryForm);
      return;
    }

    // Validate customer details form (step2 since that's where billing details are)
    const customerDetailsForm = this.checkoutForm.get('step2') as FormGroup;
    if (customerDetailsForm && customerDetailsForm.invalid) {
      this.markFormGroupTouched(customerDetailsForm);
      this.paymentError = 'Please complete the highlighted customer and delivery details before payment.';
      this.checkoutStateService.setCurrentStep(3);
      return;
    }
    const contactFields = ['email', 'firstName', 'lastName', 'phoneNumber'];
    if (
      customerDetailsForm &&
      contactFields.some(
        (name) => !String(customerDetailsForm.get(name)?.value || '').trim(),
      )
    ) {
      this.markFormGroupTouched(customerDetailsForm);
      this.paymentError = 'Please enter a valid email, name, and phone number before payment.';
      this.checkoutStateService.setCurrentStep(3);
      return;
    }

    // Validate that a genuine shipping rate is selected and no error occurred
    const shipping = this.shippingStateService.currentState;
    if (
      shipping.loading ||
      shipping.error ||
      !shipping.options.length ||
      !shipping.selectedOption ||
      !Number.isFinite(shipping.selectedOption.totalPrice) ||
      shipping.selectedOption.totalPrice < 0
    ) {
      this.paymentError = 'A verified shipping rate is required. Please retry the shipping lookup before paying.';
      return;
    }

    // Set loading state
    this.paymentError = null;
    this.saveCheckoutDraft();
    this.isProcessingOrder = true;
    this.cdr.markForCheck();

    // Calculate accurate amounts matching Step 4 Order Summary
    const subtotal = Number(this.getOrderSubtotal().toFixed(2));
    const tax = Number(this.getOrderTax().toFixed(2));
    const downloads = Number(this.getOrderDownloads().toFixed(2));
    const shippingCost = Number(this.getOrderShipping().toFixed(2));
    const orderTotal = this.getOrderTotal();
    const formData = this.checkoutForm.value;

    const selectedOption = shipping.selectedOption;

    // Prepare order data for payment - using step2 data which contains billing info
    const currentUser = this.authService.getCurrentUser();
    const billingData = formData.step2 || {};
    const deliveryData = billingData.deliverySameAsBilling
      ? billingData
      : {
          recipientName: billingData.deliveryRecipientName,
          companyName: billingData.deliveryCompanyName,
          address: billingData.deliveryAddress,
          postalCode: billingData.deliveryPostalCode,
          city: billingData.deliveryCity,
          stateProvince: billingData.deliveryStateProvince,
          country: billingData.deliveryCountry,
          phoneNumber: billingData.deliveryPhone,
        };

    const orderData = {
      total: orderTotal,
      subtotal: subtotal,
      tax: tax,
      downloadsTotal: downloads,
      shippingCost: shippingCost,
      shipping_price: shippingCost, // For backend compatibility
      shippingServiceType: selectedOption?.serviceType || 'FEDEX_STANDARD',
      shippingServiceName: selectedOption?.serviceName || 'FedEx Standard Delivery',
      shippingCurrency: this.currencyService.currentCurrency,
      shippingQuote: {
        serviceType: selectedOption.serviceType,
        totalPrice: selectedOption.totalPrice,
        currency: selectedOption.currency,
      },
      currency: this.currencyService.currentCurrency,
      exchangeRate: this.currencyService.currentRate,
      discountAmount: Number(this.getOrderDiscount().toFixed(2)),
      appliedVoucherCode: this.appliedVoucher?.code || '',
      items: this.cart,
      email: String(billingData.email || '').trim(),
      firstName: String(billingData.firstName || '').trim(),
      lastName: String(billingData.lastName || '').trim(),
      phone: String(billingData.phoneNumber || '').trim(),
      userId: currentUser?.id, // Add user ID for order tracking

      // Include billing details for order creation
      billingDetails: {
        accountType: currentUser
          ? getUserCheckoutBuyerType(currentUser)
          : billingData.accountType === 'company'
            ? 'company'
            : 'individual',
        companyName: billingData.companyName || '',
        industrySector: billingData.industrysector || '',
        vatNumber: billingData.vatNumber || '',
        postcodeTown: billingData.postcodeTown || '',
        stateProvince: billingData.stateProvince || '',
        postalCode: billingData.postalCode || '',
        city: billingData.city || '',
        otherCountry: billingData.otherCountry || '',
        otherAddressInfo: billingData.otherAddressInfo || '',
        firstName: billingData.firstName || '',
        lastName: billingData.lastName || '',
        phoneNumber: billingData.phoneNumber || '',
        email: billingData.email || '',
        orderingParty: billingData.orderingParty || '',
        noteBrocom: billingData.noteBrocom || '',
        departmentOrder: billingData.departmentOrder || ''
      },
      deliveryDetails: {
        recipientName: deliveryData.recipientName || `${billingData.firstName || ''} ${billingData.lastName || ''}`.trim(),
        companyName: deliveryData.companyName || billingData.companyName || '',
        address: deliveryData.address || deliveryData.postcodeTown || '',
        postalCode: deliveryData.postalCode || '',
        city: deliveryData.city || '',
        stateProvince: deliveryData.stateProvince || '',
        country: deliveryData.otherCountry || deliveryData.country || '',
        phoneNumber: deliveryData.phoneNumber || billingData.phoneNumber || '',
      },
      legalConsentAccepted: formData.step4?.acceptTerms === true,
    };

    // Debug log removed

    // Process payment with Paynow
    this.paynowService.processOrderPayment(orderData).subscribe({
      next: (paymentResponse) => {
        // Debug log removed

        if (paymentResponse.redirectUrl && paymentResponse.paymentId) {
          if (formData.step4?.subscribeNewsletter === true && billingData.email) {
            try {
              sessionStorage.setItem(
                `brocom_pending_newsletter_${paymentResponse.paymentId}`,
                billingData.email,
              );
            } catch (error) {
              console.error('Could not save checkout newsletter consent:', error);
            }
          }
          window.location.href = paymentResponse.redirectUrl;
        } else {
          this.isProcessingOrder = false;
          this.paymentError =
            'We could not start the payment session. Your details and cart are saved; please try again.';
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        this.isProcessingOrder = false;

        let errorMessage = 'Payment could not be started. Your details and cart are saved; please try again.';
        if (
          error.status === 409 &&
          error.error?.code === 'CART_PRICES_CHANGED' &&
          Array.isArray(error.error?.updatedProducts)
        ) {
          this.cartService.updateProductPricing(error.error.updatedProducts);
          this.refreshShippingRate();
          errorMessage =
            error.error.message ||
            'Product prices changed. Review the updated basket and try payment again.';
        } else if (
          error.status === 409 &&
          error.error?.code === 'CART_STOCK_CHANGED' &&
          Array.isArray(error.error?.updatedProducts)
        ) {
          this.cartService.updateProductStock(error.error.updatedProducts);
          this.refreshShippingRate();
          errorMessage =
            error.error.message ||
            'Product availability changed. Review the updated basket and try payment again.';
        } else if (error.status === 409) {
          this.refreshShippingRate();
          errorMessage =
            'The shipping price changed before payment. We are refreshing the verified rate; please review it and try again.';
        } else if (error.status === 408 || error.name === 'TimeoutError') {
          errorMessage =
            'The payment request timed out, so we could not confirm whether a session was created. Check your payment status before retrying. Your details and cart are saved.';
        } else if (error.status === 0 || !error.status) {
          errorMessage =
            'A network error prevented us from confirming the payment session. Check your connection and payment status before retrying. Your details and cart are saved.';
        } else if (error.error?.message) {
          errorMessage = `${error.error.message} Your details and cart are saved; please try again.`;
        } else if (error.message) {
          errorMessage = `${error.message} Your details and cart are saved; please try again.`;
        }

        this.paymentError = errorMessage;
        this.cdr.markForCheck();
      }
    });
  }

  private refreshShippingRate(): void {
    const billing = this.step2FormGroup.getRawValue();
    const delivery = billing.deliverySameAsBilling
      ? {
          otherCountry: billing.otherCountry,
          postalCode: billing.postalCode,
          city: billing.city,
          street: billing.postcodeTown,
          state: billing.stateProvince,
        }
      : {
          otherCountry: billing.deliveryCountry,
          postalCode: billing.deliveryPostalCode,
          city: billing.deliveryCity,
          street: billing.deliveryAddress,
          state: billing.deliveryStateProvince,
        };
    this.shippingStateService.fetchRates(
      this.cart,
      delivery.otherCountry,
      delivery.postalCode,
      delivery.city,
      delivery.street,
      delivery.state,
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getCartContent() {
    this.cart = this.cartService.getCart();
    // Debug info removed
    // Debug logs removed

    // Fix any cart items that have missing prices
    this.fixCartPrices();
  }

  private fixCartPrices(): void {
    let needsUpdate = false;

    this.cart.forEach((item: any, index: number) => {
      // If item doesn't have a price but has priceHT, use that
      if (!item.price && item.priceHT) {
        item.price = item.priceHT;
        needsUpdate = true;
        // Debug log removed
      }
      // If item doesn't have price or priceHT but product has priceHT, use that
      else if (!item.price && !item.priceHT && item.product?.priceHT) {
        item.price = item.product.priceHT;
        item.priceHT = item.product.priceHT;
        needsUpdate = true;
        // Debug log removed
      }
    });

    // Update the cart in storage if we made changes
    if (needsUpdate) {
      // Debug log removed
      this.cartService.clearCart();
      this.cart.forEach((item: any) => {
        this.cartService.addToCart(item);
      });
      // Refresh cart after update
      this.cart = this.cartService.getCart();
    }
  }

  previousStep(): void {
    if (this.currentStep === 1) {
      // If on the first checkout step (Shopping Basket), go back to cart page
      this.checkoutStateService.backToCart();
      this.router.navigate(['/cart']);
    } else if (this.currentStep === 3 && this.authService.isLoggedIn()) {
      // If user is already authenticated, skip step 2 (Login/Guest) and go directly back to step 1 (Shopping Basket)
      this.checkoutStateService.setCurrentStep(1);
      this.scrollToTop();
    } else if (this.currentStep > 1) {
      // Otherwise, go to previous step in checkout
      const newStep = this.currentStep - 1;
      this.checkoutStateService.setCurrentStep(newStep);
      this.scrollToTop();
    }
  }

  goToStep(stepId: number): void {
    if (stepId === 2 && this.authService.isLoggedIn()) {
      stepId = 3;
    }
    if (stepId >= 1 && stepId <= this.steps.length - 1) {
      this.checkoutStateService.setCurrentStep(stepId);
      this.scrollToTop();
    }
  }

  /**
   * Smoothly scrolls to the stepper container (center of page)
   */
  private scrollToTop(): void {
    // Use a small delay to ensure DOM updates are complete
    setTimeout(() => {
      const summaryHeader = this.currentStep === 4
        ? document.getElementById('order-summary-header')
        : null;
      const top = summaryHeader
        ? Math.max(0, summaryHeader.getBoundingClientRect().top + window.scrollY - 16)
        : 0;

      window.scrollTo({
        top,
        left: 0,
        behavior: 'smooth'
      });
    }, 100);
  }

  handleQuantityChange(event: {
    action: 'increment' | 'decrement';
    item: any;
  }) {
    event.action === 'increment'
      ? this.incrementQuantity(event.item)
      : this.decrementQuantity(event.item);
  }
  decrementQuantity(product: any) {
    const quantity = getCartItemMinimumQuantity(product);
    const currentQuantity = Number(product?.quantity);
    const newQuantity = currentQuantity % quantity === 0
      ? Math.max(quantity, currentQuantity - quantity)
      : Math.max(quantity, Math.floor(currentQuantity / quantity) * quantity);

    if (newQuantity < currentQuantity && newQuantity >= quantity) {
      this.cartService.updateCart(product?.id, newQuantity);
    }
  }

  incrementQuantity(product: any) {
    const quantity = getCartItemMinimumQuantity(product);
    const currentQuantity = Number(product?.quantity);
    const newQuantity = currentQuantity < quantity
      ? quantity
      : currentQuantity % quantity === 0
        ? currentQuantity + quantity
        : Math.ceil(currentQuantity / quantity) * quantity;
    if (newQuantity > getCartItemMaximumQuantity(product)) {
      this.paymentError = `The maximum quantity per product is ${getCartItemMaximumQuantity(product)} units.`;
      this.cdr.markForCheck();
      return;
    }
    this.cartService.updateCart(product?.id, newQuantity);
  }
  handleStepChange(step: number) {
    if (step === -1) {
      this.previousStep();
    } else {
      this.nextStep();
    }
  }
  removeItem(itemId: string): void {
    this.cartService.removeFromCart(itemId);
    // Debug log removed
  }
  deleteItem(itemId: string): void {
    this.removeItem(itemId);
  }
  refreshAuthState(): void {
    // Debug log removed
    const isAuthenticated = this.authService.isLoggedIn();
    const currentUser = this.authService.getCurrentUser();

    // Debug logs removed

    if (isAuthenticated && currentUser && this.currentStep === 2) {
      // Debug log removed
      setTimeout(() => {
        this.checkoutStateService.setCurrentStep(3);
        this.cdr.detectChanges();
      }, 100);
    }
  }
}
